// Global Instructions Rule Applied!
//
// Relationship tab — who owns this account and what we know about the
// relationship (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md Phase 0). Three
// standard sections stacked in the tab's own scroller: the team name the
// LP is greeted by, the owners, and the relationship facts. The two fetching
// sections hand their refetch up so the form's Refresh drives both (design
// patterns: data freshness).
//
// Team name (owner 2026-09-14): accounts.team_name is the {{team_name}}
// greeting for team-mode email dispatch, which falls back to the account
// name when blank. Until now nothing in the app edited it. One InputManager
// text field with auto-patch (PATCH /accounts), the way the Details tab
// edits the account's other columns; the FUND MANAGEMENT → LPs page shows
// which Active LPs still lack one.
//
// Fundraising History moved to the Funds tab (owner, 2026-09-06). It landed
// here in Phase 10 because it is standing knowledge rather than an action
// item, and that reasoning holds — but it belongs beside the commitments it
// is the other half of, and that tab is now gated on the LP role rather than
// on having a commitment, so the history reaches every LP either way.
//
// Agent Drafts moved off this tab (owner, 2026-09-04). A draft is an action
// item, so the capped copy went to the Dashboard under the LP signals strip
// and the full list with Show history went to the LP Agent tab, beside the
// signals that produced it. This tab is what its name says: ownership and
// standing knowledge.
import React, { useCallback, useMemo, useRef } from 'react'
import { Typography } from 'antd'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, type TextFieldConfig } from '../../../../../core/components/InputManager/types.ts'
import { AccountRecord, AccountRecordFields } from '../../../../../state_manager/data_models/account_record.ts'
import { AccountOwnersSection } from '../../../../relationships/components/AccountOwnersSection.tsx'
import { RelationshipFactsSection } from '../../../../relationships/components/RelationshipFactsSection.tsx'

const { Text } = Typography

interface RelationshipTabProps {
  recordId?: string | null
  currentCompany?: AccountRecord | null
  formData?: Record<string, any>
  handleFieldChange?: (field: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  onRefreshReady?: (refreshFn: () => Promise<void>) => void
}

export function RelationshipTab({
  recordId,
  currentCompany,
  formData,
  handleFieldChange,
  onPatchSuccess,
  onRefreshReady
}: RelationshipTabProps) {
  const ownersRef = useRef<(() => Promise<void>) | null>(null)
  const factsRef = useRef<(() => Promise<void>) | null>(null)

  const refreshAll = useCallback(async () => {
    await Promise.all([ownersRef.current?.(), factsRef.current?.()])
  }, [])

  React.useEffect(() => {
    if (onRefreshReady) onRefreshReady(refreshAll)
  }, [onRefreshReady, refreshAll])

  const data: Record<string, any> = currentCompany || formData || {}
  const teamNameFields = useMemo<TextFieldConfig[]>(
    () => [
      {
        key: AccountRecordFields.accounts__team_name,
        label: 'Team name',
        type: FieldTypeEnum.TEXT,
        value: data[AccountRecordFields.accounts__team_name] || '',
        editable: true,
        placeholder: 'e.g. Acme Ventures team',
        patchEndpoint: 'accounts'
      }
    ],
    [data[AccountRecordFields.accounts__team_name]]
  )

  return (
    <div data-debug='company-relationship-tab' data-component='RelationshipTab' className='h-full overflow-auto'>
      <div className='flex flex-col gap-2'>
        <FormSection title='Team name' titleColor='blue'>
          <div data-debug='company-team-name-content'>
            <InputManager
              inputMode='read'
              labelPosition='inline'
              fields={teamNameFields}
              onChange={(key, value) => handleFieldChange?.(key, value)}
              enableAutoPatch={!!recordId}
              patchId={recordId ?? undefined}
              recordId={recordId ?? undefined}
              onPatchSuccess={onPatchSuccess}
              className='company-team-name-fields'
              fieldWrapping={true}
              currentRecord={data}
            />
            <Text type='secondary' className='text-xs'>
              The greeting for team-mode emails ({'{{team_name}}'}); the account name is used when this is blank.
            </Text>
          </div>
        </FormSection>
        <AccountOwnersSection accountId={recordId} onRefreshReady={(fn) => { ownersRef.current = fn }} />
        <RelationshipFactsSection accountId={recordId} onRefreshReady={(fn) => { factsRef.current = fn }} />
      </div>
    </div>
  )
}

export default RelationshipTab
